import {
  getAdditionalData,
  getVariant,
  getVariantAdditionalData
} from 'services/ApiProductPageService';
import { selectedAddonProductSelectors } from './selector';
import { AdditionalProductData } from 'models/AdditionalProductData';
import { Action, Dispatch } from 'redux';
import { PageState } from 'models/PageState';
import { hasElements } from 'utils';
import batchAction from 'store/batching';
import { createHref } from 'components/Shared/server';
import { historyReplaceState } from 'store/history/actions';
import { addToCart, AddToCartArgs } from 'components/Cart/actions';
import { AddonProduct } from 'models/AddonProduct';
import { getConfigurator } from 'views/ProductPage/configurators/selector';
import { setPageLoadingStatus } from 'components/Pagelayout/actions';
import { LoadingStatus } from 'models/LoadingStatus';
import { AddItemResponse } from 'models/AddItemResponse';
import { RelatedProduct } from 'models/RelatedProduct';
import { isProductPage, ProductPageModel } from 'models/ProductPageModel';
import { PackageProduct } from 'models/PackageProduct';
import { RedReview } from 'models/RedReview';
import { RelationshipType } from 'models/RelationshipType';
import { SideEffect, ThunkDispatch } from 'components/Shared/connect';
import { getSelectedAddonServices } from './addonServices/selector';
import { CartItem } from 'models/CartItem';
import { CartState } from 'components/Cart/reducer';
import { GtmAddToCartLocation } from 'store/googleTagManager/types';
import { IProductPageExtendedModel } from 'models/IProductPageExtendedModel';
import { AddItemRequest } from 'models/AddItemRequest';
import { AddonGroup } from 'models/AddonGroup';

const prefix = 'productPage/';
export const productPageActionPrefix = prefix;
export const PRODUCT_PAGE_LOADED = `${prefix}PAGE_LOADED`;
export const PRODUCT_PAGE_ADDITIONAL_DATA_SET = `${prefix}ADDITIONAL_INFO`;
export const PRODUCT_PAGE_VARIANT_DATA_SET = `${prefix}VARIANT_DATA_SET`;
export const PRODUCT_PAGE_VARIANT_RESET = `${prefix}VARIANT_RESET`;
export const PRODUCT_PAGE_VARIANT_LOADING_STATUS = `${prefix}VARIANT_LOADING_STATUS`;
export const BIG_MEDIA_ACTIVE = `${prefix}BIG_MEDIA_ACTIVE`;
export const BIG_MEDIA_INACTIVE = `${prefix}BIG_MEDIA_INACTIVE`;
export const BUNDLED_PRODUCT_SELECT = `${prefix}BUNDLED_PRODUCT_SELECT`;
export const PRODUCT_PAGE_PRODUCT_ADDED_TO_CART = `${prefix}PRODUCT_ADDED_TO_CART`;
export const ADDON_SERVICE_SELECT = `${prefix}SELECT_ADDON_SERVICE`;
export const ADDON_SERVICE_DESELECT = `${prefix}DESELECT_ADDON_SERVICE`;
export const ADDON_SERVICE_DESELECT_ALL = `${prefix}RESET_ADDON_SERVICE`;
export const ADDON_SERVICE_ZIP_CODE_SET = `${prefix}SET_ADDON_SERVICE_ZIP_CODE`;

export interface ProductPageAdditionalDataAction extends Action {
  data: AdditionalProductData;
}

export interface ProductPageVariantDataAction extends Action {
  data: IProductPageExtendedModel;
}

export interface ProductPageVariantLoadingStatusAction extends Action {
  status: LoadingStatus;
}

export interface ProductPageBundledProductAction extends Action {
  selected: boolean;
}

export interface ProductPageReviews extends Action {
  reviews: RedReview[];
}

export interface AddonServiceSelectAction extends Action {
  selectedId: string;
}

export interface AddonServiceZipCodeSetAction extends Action {
  zipCode: string;
}

export const bigMediaActive = (): Action => ({
  type: BIG_MEDIA_ACTIVE
});

export const bigMediaInactive = (): Action => ({
  type: BIG_MEDIA_INACTIVE
});

export const bundledProductSelect = (selected: boolean): ProductPageBundledProductAction => ({
  type: BUNDLED_PRODUCT_SELECT,
  selected
});

export const resetSelectedVariant = () => ({
  type: PRODUCT_PAGE_VARIANT_RESET
});

export const productPageLoaded = (): Action => ({
  type: PRODUCT_PAGE_LOADED
});

export const productPageAdditionalDataSet = (
  data: AdditionalProductData
): ProductPageAdditionalDataAction => ({
  type: PRODUCT_PAGE_ADDITIONAL_DATA_SET,
  data
});

type Variant = Complete<RelatedProduct, 'variantId'>;
const getAccessoryIds = (
  accessories?: RelatedProduct[]
): { accessoryIds: string[]; variantAccessoryIds: string[] } => {
  if (!hasElements(accessories)) {
    return { accessoryIds: [], variantAccessoryIds: [] };
  }

  return {
    accessoryIds: accessories.filter(a => !a.variantId).map(a => a.productId),
    variantAccessoryIds: accessories
      .filter((a): a is Variant => Boolean(a.variantId))
      .map(a => `${a.productId}-${a.variantId}`)
  };
};

const getPackageProductIds = (packageProduct?: PackageProduct): string[] => {
  if (packageProduct === undefined || packageProduct.products === undefined) {
    return [];
  }

  return packageProduct.products.map(p => p.productId);
};

export const getProductPageAdditionalInfo =
  () => (dispatch: Dispatch, getState: () => PageState) => {
    const { currentPage } = getState();
    if (!isProductPage(currentPage)) {
      return Promise.resolve();
    }

    const {
      productId,
      series,
      brandName,
      mainWebCatalog,
      price: { listPrice },
      saleOrderCount,
      energyLabelVariant,
      relevanceWithinCategory
    } = currentPage.product;
    return getAdditionalData(productId, {
      limitOtherBrands: false,
      brand: brandName,
      series,
      webCategory: mainWebCatalog,
      listPrice,
      saleOrderCount,
      energyLabelVariant,
      packageProductIds: getPackageProductIds(currentPage.packageProduct),
      ...getAccessoryIds(currentPage.accessories),
      relevanceWithinCategory
    }).then(res => {
      dispatch(productPageAdditionalDataSet(res));
    });
  };

const getProductPageVariantAdditionalInfo =
  (variant: string) => (dispatch: Dispatch, getState: () => PageState) => {
    const { currentPage } = getState();
    if (!isProductPage(currentPage)) {
      return Promise.resolve();
    }
    const {
      productId,
      series,
      brandName,
      mainWebCatalog,
      price: { listPrice },
      saleOrderCount,
      energyLabelVariant
    } = currentPage.product;

    return getVariantAdditionalData(productId, variant, {
      limitOtherBrands: false,
      brand: brandName,
      series,
      webCategory: mainWebCatalog,
      listPrice,
      ...getAccessoryIds(currentPage.accessories),
      saleOrderCount,
      energyLabelVariant
    }).then(res => {
      dispatch(productPageAdditionalDataSet(res));
    });
  };

export const productPageVariantDataSet = (
  data: ProductPageModel
): ProductPageAdditionalDataAction => ({
  type: PRODUCT_PAGE_VARIANT_DATA_SET,
  data
});

const setVariantLoadingStatus = (status: LoadingStatus): ProductPageVariantLoadingStatusAction => ({
  type: PRODUCT_PAGE_VARIANT_LOADING_STATUS,
  status
});

const PRODUCT_ID = /\/p-[0-9]+$/;
const VARIANT_ID = /\/p-[0-9]+(-[0-9]+)$/;
export const getVariantData =
  (product: string, variant: string) => (dispatch: ThunkDispatch, getState: () => PageState) => {
    const state = getState();
    const currentPage = state.currentPage as ProductPageModel;

    if (currentPage.variantLoadingStatus === LoadingStatus.Fetching) {
      return;
    }

    dispatch(setPageLoadingStatus(LoadingStatus.Fetching));
    dispatch(setVariantLoadingStatus(LoadingStatus.Fetching));

    void getVariant(product, variant).then(res => {
      const loc = getState().location;
      const url = createHref({
        ...loc,
        pathname: VARIANT_ID.exec(loc.pathname)
          ? `${loc.pathname.substr(0, loc.pathname.lastIndexOf('-'))}-${variant}`
          : PRODUCT_ID.exec(loc.pathname)
            ? `${loc.pathname}-${variant}`
            : `${loc.pathname}/p-${product}-${variant}`
      });
      dispatch(
        batchAction(
          productPageVariantDataSet(res),
          historyReplaceState({ url }),
          setPageLoadingStatus(LoadingStatus.Completed),
          getProductPageVariantAdditionalInfo(variant),
          setVariantLoadingStatus(LoadingStatus.Completed)
        )
      );
    });
  };

export interface ProductPageAddCartItem {
  id: string;
  quantity: number;
}

type ProductPageAddToCartProps = {
  item: ProductPageAddCartItem;
  gtmBtnLocation?: GtmAddToCartLocation;
  gtmItemList?: string;
};

const productAddedToCart = (): Action => ({
  type: PRODUCT_PAGE_PRODUCT_ADDED_TO_CART
});

type GenericAddToCartResponse<T> = T extends AddItemResponse
  ? SideEffect<AddItemResponse>
  : T extends AddToCartResponse
    ? Promise<AddToCartResponse>
    : never;

interface GetAddToCartProps<T> {
  addToCartFn: (args: AddToCartArgs) => GenericAddToCartResponse<T>;
  currentPage: ProductPageModel;
  cartItems: CartItem[];
  subItemRelations: CartState['subItemRelations'];
  amount: number;
  fullProductId: string;
  gtmBtnLocation?: GtmAddToCartLocation;
  gtmItemList?: string;
}
interface GetAddItemRequestProps {
  currentPage: ProductPageModel;
  cartItems: CartItem[];
  subItemRelations: CartState['subItemRelations'];
  amount: number;
  fullProductId: string;
  inDrawer?: boolean;
  addons?: Record<string, AddonGroup>;
  configuratorVariables?: Record<string, string>;
}

export const getAddItemRequest = ({
  currentPage,
  cartItems,
  amount,
  fullProductId,
  subItemRelations,
  inDrawer,
  addons,
  configuratorVariables
}: GetAddItemRequestProps): AddItemRequest => {
  const config = getConfigurator(currentPage);
  const configVars = configuratorVariables
    ? configuratorVariables
    : config
      ? config.values || {}
      : undefined;

  const addonProducts = getAddons(amount, currentPage, inDrawer, addons);

  const addonServiceProducts = getSelectedAddonServices(currentPage);
  const combinedAddons = [...addonProducts, ...addonServiceProducts];
  const { bundledProduct } = currentPage;

  const bundleId = bundledProduct && bundledProduct.selected ? bundledProduct.productId : undefined;

  const cartIds: string[] = cartItems.reduce((ids, cartItem) => {
    const subItem = subItemRelations[cartItem.cartItemId];
    const subItemIds =
      subItem && subItem.type === RelationshipType.Subitem
        ? subItem.items.map(item => item.productId)
        : [];
    return [...ids, cartItem.productId, ...subItemIds];
  }, []);

  const accessoryIds = currentPage.accessories?.map(a => a.productId);
  const filteredAccessoryIds = accessoryIds?.filter(id => !cartIds.includes(id));

  return {
    id: fullProductId,
    quantity: amount,
    getRecommendations: !inDrawer,
    configuratorVariables: configVars,
    addonProducts: combinedAddons,
    bundleId,
    cartIds,
    accessoryIds: filteredAccessoryIds,
    isBackOrder: currentPage.product.meta?.isBackOrder,
    backInStockDate: currentPage.product.meta?.backInStockDate
  };
};

const getAddons = (
  amount: number,
  currentPage: ProductPageModel,
  inDrawer?: boolean,
  addons: Record<string, AddonGroup> = {}
): AddonProduct[] => {
  if (!inDrawer) {
    return selectedAddonProductSelectors
      .filter(s => s.name !== 'bundled-product') // filter out bundle since it is sent as bungleId
      .map(({ selector }) => selector(currentPage))
      .reduce((a, b) => a.concat(b), [])
      .map<AddonProduct>(p => ({
        id: p.productId,
        quantity: p.quantity ? p.quantity.amount : amount
      }));
  }

  return Object.values(addons)
    .flatMap(addon => addon.products)
    .filter(product => product.selected)
    .map<AddonProduct>(p => ({
      id: p.productId,
      quantity: p.quantity ? p.quantity.amount : amount
    }));
};

const getAddToCart = <T>({
  addToCartFn: fn,
  currentPage,
  cartItems,
  amount,
  fullProductId,
  subItemRelations,
  gtmBtnLocation,
  gtmItemList
}: GetAddToCartProps<T>): (() => GenericAddToCartResponse<T>) => {
  const item = getAddItemRequest({
    currentPage,
    cartItems,
    amount,
    fullProductId,
    subItemRelations
  });

  return (): GenericAddToCartResponse<T> =>
    fn({
      input: item,
      btnLocation: gtmBtnLocation,
      gtmListName: gtmItemList
    });
};

interface AddToCartResponse {
  data: AddItemResponse;
}

export const productPageAddToCart =
  ({ item, gtmBtnLocation, gtmItemList }: ProductPageAddToCartProps) =>
  (dispatch: ThunkDispatch, getState: () => PageState): Promise<AddItemResponse> => {
    const { currentPage, cart } = getState();
    const { id, quantity } = item;
    if (!isProductPage(currentPage)) {
      return Promise.resolve({ cart, success: false, message: '' });
    }

    const addToCartAction = getAddToCart<AddItemResponse>({
      addToCartFn: addToCart,
      currentPage,
      cartItems: cart.cartItems,
      subItemRelations: cart.subItemRelations,
      amount: quantity,
      fullProductId: id,
      gtmBtnLocation,
      gtmItemList
    });

    return dispatch(addToCartAction()).then(response => {
      dispatch(productAddedToCart());
      return response;
    });
  };

export const selectAddonService = (selectedId: string): AddonServiceSelectAction => ({
  type: ADDON_SERVICE_SELECT,
  selectedId
});

export const deselectAddonService = (selectedId: string): AddonServiceSelectAction => ({
  type: ADDON_SERVICE_DESELECT,
  selectedId
});

export const deselectAllAddonServices = (): AddonServiceSelectAction => ({
  type: ADDON_SERVICE_DESELECT_ALL,
  selectedId: ''
});

export const setAddonServiceZipCode = (zipCode: string): AddonServiceZipCodeSetAction => ({
  type: ADDON_SERVICE_ZIP_CODE_SET,
  zipCode
});
